import { Component, Input, forwardRef, EventEmitter, Output, ViewChildren, QueryList, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject, Subscription, filter } from 'rxjs';
import { isEqual } from 'lodash';

import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';

import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';

import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

import { Mention } from '../../models/mention';
import { MentionBadge, MentionCardComponent, mentionBadgesAll } from '../MentionCard/mention-card.component';
import { TranslateMentionsService } from '../../services/translate-mentions.service';
import { MentionContentCustomizationEnum } from '../../enums/mention-content-customization.enum';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ManageMentionOrganizationDialogComponent } from '../ManageMentionOrganization/manage-mention-organization-dialog.component';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';


@Component({
    selector: 'agpr-mention-list',
    templateUrl: './mention-list.component.html',
    styleUrls: ['./mention-list.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MentionListComponent),
        multi: true
    }],
    standalone: false
})
export class MentionListComponent implements ControlValueAccessor, OnChanges, OnDestroy {

    @Input() id: string;
    @Input() set mentions(value: Mention[]) {
        this._mentions = value;

        if (!value) {
            this.loading = true;
            this.ungroupedMentionsList = [];
        } else {
            this.loading = false;
            this.ungroupedMentionsList = value.slice();

            if (this._groupDuplicates) {
                this.sortMentionsByDuplicateId();
            }
        }
    }
    @Input() mentionSearch: MentionSearchModel;
    @Input() canExpand: boolean = true;
    @Input() checkboxDisabled: boolean = false;
    @Input() checkboxCanBeDisabledWhenChecked: boolean = true;
    @Input() compact: boolean = false;
    @Input() showTranslate: boolean = false;
    @Input() showAllImages: boolean = false;
    @Input() showMentionIndex: boolean = false;
    @Input() readonly: boolean = false;
    @Input() set groupDuplicates(group: boolean) {
        this._groupDuplicates = group;

        if (group) {
            this.sortMentionsByDuplicateId();
        } else {
            this._mentions = this.ungroupedMentionsList.slice();
        }
    }
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() badges: MentionBadge[] = mentionBadgesAll;

    @Input() enableFocus: boolean = true;
    @Input() reGetCardHeight: boolean = false;
    @Input() focusedMention: Mention;
    @Input() highlightSavedMentions: boolean;
    @Input() mentionContent: MentionContentCustomizationEnum  = MentionContentCustomizationEnum.summary;
    @Input() selectedFolder: Folder;
    @Input() savingLimitExceeded: boolean = false;
    @Input() showMentionViewIcon: boolean = false;
    @Input() useMentionCardErrorTemplate: boolean = false;
    @Input() isSyndicateMode: boolean = false;

    @Input() topLabelMention: Mention;
    @Input() topLabel: string;

    @Output() focusedMentionChange: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() mentionCardClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() outletClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() contactClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() unlockMentionClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() viewAllSyndicatesClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() selectedMentionClicked: EventEmitter<Mention[]> = new EventEmitter<Mention[]>();
    @Output() openSavingLimitExceededDialog: EventEmitter<void> = new EventEmitter<void>();

    @ViewChildren('mentionCard') mentionCards: QueryList<MentionCardComponent>;

    overlayContainer: Element;
    mentionIndexOffset: number = 0;
    selectedMentions: Mention[] = [];
    _mentions: Mention[] = [];
    mentionFlags: { [id: string]: boolean } = {};
    partialSelectionFlags: { [id: string]: boolean } = {};
    _groupDuplicates: boolean = false;
    loading: boolean = false;
    showPublishedDate: boolean = true;
    sortField: SearchCriteriaSortFieldEnum;

    userLanguage: string;
    concurrentTranslation: number = 0;
    openAiPermission: boolean = false;

    private ungroupedMentionsList: Mention[] = [];
    private subscriptions: Subscription[] = [];

    readonly maxConcurrentTranslation: number = 3;
    readonly buttonTypesEnum = ButtonTypesEnum;

    onChange: (value: Mention[]) => void;
    onTouched: () => void;

    constructor(
        private coreDialogService: CoreDialogService,
        private mentionSearchService: MentionSearchService,
        protected translateService: TranslateMentionsService,
        private currentUserService: CurrentUserStateService,
    ) {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.mentionSearchService.getPageCriteria().subscribe(
                criteria => {
                    if (criteria) {
                        this.mentionIndexOffset = (criteria.page - 1) * criteria.pageSize;
                    }
                }
            ),
            this.translateService.getLanguage().subscribe((language) => this.userLanguage = language),
            this.translateService.getConcurrentTranslation().subscribe(
                (concurrentTranslation) => this.concurrentTranslation = concurrentTranslation
            ),
            this.currentUserService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.mentionSearchService.getSearchCriteria().pipe(filter(x => !!x)).subscribe(searchCriteria => {
                this.showPublishedDate = searchCriteria.sortField !== SearchCriteriaSortFieldEnum.indexed_date;
                this.sortField = searchCriteria.sortField;
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        // we don't want to include the MentionSearchService in translateService (because it's in common)
        // so we can set the highlightKeywords here
        if (!isEqual(changes?.highlightKeywords?.currentValue, changes?.highlightKeywords?.previousValue)) {
            this.translateService.setHighLightedKeywords(this.highlightKeywords);
        }
    }

    writeValue(mentions: Mention[]): void {
        this.selectedMentions = mentions ? mentions.slice() : [];
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the model has changed
    */
    registerOnChange(fn: (value: Mention[]) => void): void {
        this.onChange = fn;
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the input has been touched
    */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public mentionTrackBy(index, mention) {
        return mention ? mention.id : null;
    }

    checkedStateChange(): void {
        this.onChange(this.selectedMentions);
        this.selectedMentionClicked.emit(this.selectedMentions);

        this._mentions.forEach((mention) => {
            if (mention.duplicateGroupId) {
                this.mentionFlags[mention.duplicateGroupId] = this.isDuplicateGroupSelected(mention.duplicateGroupId);
                this.partialSelectionFlags[mention.duplicateGroupId] = this.isDuplicateGroupPartiallySelected(mention.duplicateGroupId);
            }
        });
    }

    setFocusedMention(mention: Mention): void {
        if (
            !this.enableFocus ||
            (
                this.focusedMention.mentionId &&
                this.focusedMention.mentionId === mention.mentionId
            )
        ) {
            return;
        }
        this.focusedMention = mention;
        this.focusedMentionChange.emit(mention);
    }

    isDuplicateGroupSelected(duplicateGroupId: string): boolean {
        if (!duplicateGroupId) {
            return false;
        }
        const groupMentions = this._mentions.filter(mention => mention.duplicateGroupId === duplicateGroupId);
        const childMentions = groupMentions.slice(1);
        return childMentions.every(mention => this.isMentionSelected(mention));
    }

    isMentionSelected(mention: Mention): boolean {
        return this.selectedMentions.some(selected => selected && selected.id === mention.id);
    }

    onDuplicateGroupChecked(isChecked: boolean, duplicateGroupId: string): void {
        if(!duplicateGroupId) {
            return;
        }
        const groupMentions = this._mentions.filter(mention => mention.duplicateGroupId === duplicateGroupId);
        const childMentions = groupMentions.slice(1);

        if(isChecked){
            childMentions.forEach(mention => {
                if (!this.isMentionSelected(mention)) {
                    this.selectedMentions.push(mention);
                }
            });
        } else {
            this.selectedMentions = this.selectedMentions.filter(
                mention => !(mention.duplicateGroupId === duplicateGroupId && groupMentions.indexOf(mention) > 0)
            );
        }
        this.selectedMentions = [...this.selectedMentions];
        this.checkedStateChange();
    }

    isDuplicateGroupPartiallySelected(duplicateGroupId: string): boolean {
        if(!duplicateGroupId ) {
            return false;
        }
        const groupMentions = this._mentions.filter(mention => mention.duplicateGroupId === duplicateGroupId);
        const childMentions = groupMentions.slice(1);

        const someSelected = childMentions.some(mention => this.isMentionSelected(mention));
        const allSelected = childMentions.every(mention => this.isMentionSelected(mention));
        return someSelected && !allSelected;
    }

    sortMentionsByDuplicateId(): void {
        const mentions = this.ungroupedMentionsList;
        if (!mentions || mentions.length < 1) {
            this._mentions = [];

            return;
        }

        const sortedMentions = [];
        const usedDuplicateIds = [];

        mentions.forEach(mention => {
            if (!mention.duplicateGroupId) {
                sortedMentions.push(mention);
            } else {
                if (usedDuplicateIds.includes(mention.duplicateGroupId)) {
                    return;
                }
                usedDuplicateIds.push(mention.duplicateGroupId);
                sortedMentions.push(
                    ...mentions.filter(other => mention.duplicateGroupId === other.duplicateGroupId)
                );
            }
        });

        this._mentions = sortedMentions;
    }

    onCardKeydown(event: KeyboardEvent, mention: Mention) {
        const target = event.target as HTMLElement;
        if ((event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) && target.classList.contains('agpr-mentionList-mention')) {
            this.clickMentionCard(mention);
        }
    }

    translate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            const mentionCard = this.mentionCards.find(element => {
                return element.mention.id === mention.id;
            });

            if (mentionCard) {
                this.translateService.setupChatAI(mention, null, true, true);
            }
        }
    }

    revertTranslate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            this.translateService.setDefaultTextAlign(mention);
            mention.showTranslated = false;
            mention.translation = null;

            const mentionCard = this.mentionCards.find(element => element.mention.id === mention.id);
            if (mentionCard && !mentionCard.expanded) {
                mentionCard.updateCollapsedHeight();
            }
        }
    }

    openManageMentionOrganizationDialog(mention: Mention, event: any) {
        event.stopPropagation();
        if (this.enableFocus) {
            this.setFocusedMention(mention);
        }
        if (this.savingLimitExceeded) {
            this.openSavingLimitExceededDialog.emit();
            return;
        }
        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                mention
            },
            outputs: {
                dialogClose: (newMention: Mention) => {
                    if (newMention) {
                        const {folderIds = [], tagIds = []} = newMention;
                        mention.folderIds = folderIds;
                        mention.tagIds = tagIds;
                    }
                }
            }
        });
    }

    clickMentionCard(mention: Mention): void {
        this.setFocusedMention(mention);
        this.mentionCardClicked.emit(mention);
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
